import React, { useEffect, useMemo, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Search } from "lucide-react";
import { Container, PageHero } from "@/components/ui-blocks";
import ProductCard from "@/components/ProductCard";
import Seo from "@/components/Seo";
import { fetchProducts, fetchCategories } from "@/services/api";

const SORTS = [
  { key: "default", label: "Featured" },
  { key: "price_asc", label: "Price · Low to High" },
  { key: "price_desc", label: "Price · High to Low" },
  { key: "title", label: "Title · A to Z" },
];

export default function Shop() {
  const { category: categoryParam } = useParams();
  const navigate = useNavigate();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState("");
  const [sort, setSort] = useState("default");
  const [cats, setCats] = useState([]);
  // Trust the URL slug directly — categories load asynchronously, and any
  // admin-created category should work without a hard-coded list.
  const activeCat = categoryParam || "all";

  useEffect(() => {
    fetchCategories().then(setCats).catch(() => {});
  }, []);

  useEffect(() => {
    setLoading(true);
    fetchProducts({ category: activeCat, query, sort }).then((p) => {
      setProducts(p);
      setLoading(false);
    });
  }, [activeCat, query, sort]);

  const CATEGORIES = useMemo(
    () => [
      { key: "all", label: "All" },
      ...cats.map((c) => ({ key: c.slug || c.id, label: c.name })),
    ],
    [cats],
  );

  const currentCat = cats.find((c) => (c.slug || c.id) === activeCat);

  const heroTitle = useMemo(() => {
    if (activeCat === "books") return "Ace Asia Books.";
    if (activeCat === "collectibles") return "Ace Asia Collectibles.";
    if (currentCat) return `${currentCat.name}.`;
    return "The Shop.";
  }, [activeCat, currentCat]);

  const heroSubtitle = useMemo(() => {
    if (activeCat === "books")
      // Delivery availability is governed by the shipping zones now, so this
      // copy stays on the launch date and says nothing about where we ship.
      return "The first edition of The Chettis of South-east Asia launches in September 2026, with online orders available from late September.";
    if (activeCat === "collectibles")
      return "A new range of tableware, teapots and architectural facades celebrating Indian Peranakan heritage — releasing 2027.";
    if (currentCat) return `Browse ${currentCat.name} from Ace Asia Productions.`;
    return "Books and heritage collectibles from Ace Asia Productions.";
  }, [activeCat, currentCat]);

  return (
    <div data-testid="shop-page">
      <Seo title="Shop" description="Browse heritage books and collectibles from Ace Asia Productions — celebrating South-east Asian culture and craft." />
      <PageHero testId="shop-hero" overline="Browse the Catalogue" title={heroTitle} subtitle={heroSubtitle} />

      {/* Filter bar */}
      <div className="sticky top-[88px] z-30 bg-[#FDFBF7]/95 backdrop-blur-md border-b border-[#6e5022]/15">
        <Container>
          <div className="py-5 flex flex-col md:flex-row md:items-center gap-4 md:gap-6">
            <div className="flex gap-2 flex-wrap">
              {CATEGORIES.map((c) => (
                <button
                  key={c.key}
                  data-testid={`shop-category-${c.key}`}
                  onClick={() => navigate(c.key === "all" ? "/shop" : `/shop/${c.key}`)}
                  className={`px-5 py-2.5 text-xs uppercase tracking-[0.18em] border transition-colors ${
                    activeCat === c.key
                      ? "bg-[#6e5022] text-[#FDFBF7] border-[#6e5022]"
                      : "border-[#6e5022]/30 text-[#6e5022] hover:bg-[#F4F1EA]"
                  }`}
                >
                  {c.label}
                </button>
              ))}
            </div>

            <div className="flex-1 flex items-center border-b border-[#6e5022]/30 focus-within:border-[#6e5022]">
              <Search className="w-4 h-4 text-[#946f36]" strokeWidth={1.5} />
              <input
                data-testid="shop-search-input"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search the catalogue"
                className="flex-1 bg-transparent px-3 py-2 text-sm outline-none placeholder-[#946f36]/60"
              />
            </div>

            <select
              data-testid="shop-sort-select"
              value={sort}
              onChange={(e) => setSort(e.target.value)}
              className="bg-transparent border border-[#6e5022]/30 px-4 py-2.5 text-xs uppercase tracking-[0.15em] focus:outline-none focus:border-[#6e5022]"
            >
              {SORTS.map((s) => (
                <option key={s.key} value={s.key}>{s.label}</option>
              ))}
            </select>
          </div>
        </Container>
      </div>

      {/* Editorial copy strip when filtered */}
      {activeCat === "books" && (
        <section className="bg-[#F4F1EA] py-12" data-testid="shop-books-editorial">
          <Container>
            <p className="text-[#4A3728] leading-relaxed max-w-4xl">
              Ace Asia founder Chantal Sajan was a senior correspondent at The
              Straits Times and a multidisciplinary designer in her own right.
              She has crafted logos for the Singapore Indian Development
              Association (SINDA) and Singapore Association of Women Lawyers
              (SAWL) among other logos for the radio stations of the
              then-Singapore Broadcasting Corporation, renamed MediaCorp. In
              1990, she won an Award of Excellence from the US-based Society
              for News Design for experimental tabloid architecture.
            </p>
          </Container>
        </section>
      )}

      {activeCat === "collectibles" && (
        <section className="bg-[#F4F1EA] py-12" data-testid="shop-collectibles-editorial">
          <Container>
            <p className="text-[#4A3728] leading-relaxed max-w-4xl">
              Ms Sajan is collaborating with a team of artisans and production
              houses to capture the essence of Indian Peranakan heritage through
              her new &lsquo;Ace Asia Collectibles&rsquo; range of tableware for release
              next year, 2027. These include teapots with mediaeval batik
              textile print designs worn by her ancestors dating back to the
              15th century in old Melaka, Malaysia.
            </p>
          </Container>
        </section>
      )}

      {/* Grid */}
      <section className="py-16 bg-[#FDFBF7]">
        <Container>
          {loading ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8" data-testid="shop-loading">
              {[...Array(4)].map((_, i) => (
                <div key={i} className="animate-pulse">
                  <div className="aspect-[4/5] bg-[#F4F1EA]" />
                  <div className="h-4 bg-[#F4F1EA] mt-4 w-1/3" />
                  <div className="h-6 bg-[#F4F1EA] mt-2" />
                </div>
              ))}
            </div>
          ) : products.length === 0 ? (
            <div className="text-center py-24" data-testid="shop-empty-state">
              <div className="font-serif-display text-3xl">No products found</div>
              <p className="text-[#4A3728] mt-3">Try clearing search or changing the category.</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8" data-testid="shop-grid">
              {products.map((p, i) => (
                <ProductCard key={p.id} product={p} index={i} />
              ))}
            </div>
          )}
        </Container>
      </section>
    </div>
  );
}
